import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Icon from './OpenPlayIcon';
import PicklingLogoMark from '../PicklingLogoMark';
import { supabase } from '../../lib/supabaseClient';
import LoginRequiredModal from '../ui/LoginRequiredModal';

const BottomTabNavigation = ({
  notificationCount = 0,
  userRole = 'player',
  sidebarCollapsed = false,
  onToggleSidebar,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const pathname = location?.pathname || '';

  const [isAuthed, setIsAuthed] = useState(false);
  const [profileRole, setProfileRole] = useState('');
  const [profileName, setProfileName] = useState('');
  const [profileAvatarUrl, setProfileAvatarUrl] = useState('');
  const [loginRequiredOpen, setLoginRequiredOpen] = useState(false);
  const [internalCollapsed, setInternalCollapsed] = useState(false);

  useEffect(() => {
    if (typeof onToggleSidebar === 'function') return;
    try {
      const saved = window.localStorage.getItem('openplay_sidebar_collapsed');
      setInternalCollapsed(saved === '1');
    } catch {
      setInternalCollapsed(false);
    }
  }, [onToggleSidebar]);

  const effectiveCollapsed = useMemo(() => {
    if (typeof onToggleSidebar === 'function') return !!sidebarCollapsed;
    return !!internalCollapsed;
  }, [internalCollapsed, onToggleSidebar, sidebarCollapsed]);

  const handleToggleCollapsed = () => {
    if (typeof onToggleSidebar === 'function') {
      onToggleSidebar();
      return;
    }

    setInternalCollapsed((prev) => {
      const next = !prev;
      try {
        window.localStorage.setItem('openplay_sidebar_collapsed', next ? '1' : '0');
      } catch {
        // ignore
      }

      try {
        window.dispatchEvent(new CustomEvent('openplay_sidebar_collapsed_change', { detail: { collapsed: next } }));
      } catch {
        // ignore
      }
      return next;
    });
  };

  useEffect(() => {
    let mounted = true;

    const sync = async () => {
      try {
        const { data } = await supabase.auth.getSession();
        if (!mounted) return;
        const authed = !!data?.session?.user;
        setIsAuthed(authed);

        const userId = data?.session?.user?.id;
        const meta = data?.session?.user?.user_metadata || {};
        setProfileAvatarUrl(String(meta?.avatar_url || '').trim());
        setProfileName(String(meta?.username || '').trim());
        if (!authed || !userId) {
          setProfileRole('');
          setProfileName('');
          setProfileAvatarUrl('');
          return;
        }

        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select('role, name')
          .eq('id', userId)
          .maybeSingle();

        if (!mounted) return;
        if (profileError) {
          setProfileRole('');
          setProfileName(String(meta?.username || '').trim());
          return;
        }
        setProfileRole(String(profile?.role || '').trim());
        const nextName = String(profile?.name || meta?.username || '').trim();
        setProfileName(nextName);
      } catch {
        if (!mounted) return;
        setIsAuthed(false);
        setProfileRole('');
        setProfileName('');
        setProfileAvatarUrl('');
      }
    };

    sync();
    const { data: sub } = supabase.auth.onAuthStateChange(() => {
      sync();
    });

    return () => {
      mounted = false;
      sub?.subscription?.unsubscribe?.();
    };
  }, []);

  const navigationItems = [
    {
      label: '발견',
      path: '/openplay/event',
      icon: 'MapIcon',
    },
    {
      label: '도구',
      path: '/openplay/tools',
      icon: 'BoltIcon',
    },
    {
      label: '프로필',
      path: '/openplay/user-profile-management',
      icon: 'UserIcon',
    },
  ];

  if (userRole === 'organizer') {
    navigationItems.push({
      label: '관리',
      path: '/openplay/organizer-dashboard',
      icon: 'ClipboardDocumentListIcon',
      badge: notificationCount,
    });
  }

  navigationItems.push(
    isAuthed
      ? {
          label: '로그아웃',
          path: null,
          icon: 'ArrowLeftOnRectangleIcon',
          action: 'logout',
        }
      : {
          label: '로그인',
          path: '/openplay/user-authentication',
          icon: 'ArrowRightOnRectangleIcon',
        }
  );

  const sidebarItems = [
    {
      label: '이벤트',
      path: '/openplay/event',
      icon: 'MapIcon',
    },
    {
      label: '커뮤니티',
      path: '/openplay/community',
      icon: 'ChatBubbleLeftRightIcon',
    },
    ...(isAuthed && profileRole === 'admin'
      ? [
          {
            label: '코트 관리',
            path: '/openplay/code-management',
            icon: 'BuildingOfficeIcon',
          },
        ]
      : []),
    ...navigationItems.filter(
      (item) =>
        typeof item?.path === 'string' &&
        Boolean(item?.path) &&
        item.path !== '/openplay/interactive-event-map' &&
        item.path !== '/openplay/event' &&
        item.path !== '/openplay/user-profile-management' &&
        item.path !== '/openplay/user-authentication'
    ),
  ];

  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
    } catch {
      // ignore
    }
    navigate('/openplay/tools');
  };

  const handleLoginRequired = () => {
    setLoginRequiredOpen(true);
  };

  const isActive = (path) => pathname === path;

  const displayName = String(profileName || '').trim() || '사용자';
  const avatarInitial = displayName ? displayName.slice(0, 1).toUpperCase() : 'U';

  return (
    <>
      <nav
        className="fixed bottom-0 left-0 right-0 z-[10000] bg-card shadow-elevation-3 md:hidden"
        style={{ paddingBottom: 'env(safe-area-inset-bottom, 16px)' }}
      >
        <div className="flex items-center justify-around h-[60px] px-4">
          {navigationItems.map((item) => {
            const active = isActive(item.path);
            const isAction = item?.action === 'logout';
            const isProfile = item?.path === '/openplay/user-profile-management';
            return (
              isAction ? (
                <button
                  key={`action-${item.label}`}
                  type="button"
                  onClick={handleLogout}
                  className={`
                    relative flex flex-col items-center justify-center gap-1
                    min-w-[64px] min-h-[44px] px-3 py-2 rounded-md
                    transition-smooth touch-target
                    text-muted-foreground hover:text-foreground
                  `}
                >
                  <div className="relative">
                    <Icon
                      name={item.icon}
                      variant="outline"
                      size={24}
                      className="transition-smooth"
                    />
                  </div>
                  <span className="text-xs font-medium caption">{item.label}</span>
                </button>
              ) : (
                (!isAuthed && isProfile) ? (
                  <button
                    key={`auth-required-${item.label}`}
                    type="button"
                    onClick={handleLoginRequired}
                    className={`
                      relative flex flex-col items-center justify-center gap-1
                      min-w-[64px] min-h-[44px] px-3 py-2 rounded-md
                      transition-smooth touch-target
                      text-muted-foreground hover:text-foreground
                    `}
                  >
                    <div className="relative">
                      <Icon
                        name={item.icon}
                        variant="outline"
                        size={24}
                        className="transition-smooth"
                      />
                    </div>
                    <span className="text-xs font-medium caption">{item.label}</span>
                  </button>
                ) : (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`
                    relative flex flex-col items-center justify-center gap-1
                    min-w-[64px] min-h-[44px] px-3 py-2 rounded-md
                    transition-smooth touch-target
                    ${active ? 'text-primary' : 'text-muted-foreground hover:text-foreground'}
                  `}
                >
                  <div className="relative">
                    <Icon
                      name={item.icon}
                      variant={active ? 'solid' : 'outline'}
                      size={24}
                      className="transition-smooth"
                    />
                    {item.badge && item.badge > 0 && (
                      <span className="absolute -top-1 -right-1 flex items-center justify-center min-w-[18px] h-[18px] px-1 text-xs font-medium text-primary-foreground bg-primary rounded-full">
                        {item.badge > 99 ? '99+' : item.badge}
                      </span>
                    )}
                  </div>
                  <span className="text-xs font-medium caption">{item.label}</span>
                </Link>
                )
              )
            );
          })}
        </div>
      </nav>

      <aside
        className={`hidden md:block fixed left-0 top-0 bottom-0 z-navigation bg-card shadow-elevation-2 transition-all duration-200 ${
          effectiveCollapsed ? 'w-[72px]' : 'w-[240px]'
        }`}
      >
        <div className="flex flex-col h-full">
          <div className={`flex items-center h-[72px] border-b border-border ${effectiveCollapsed ? 'px-3 justify-center' : 'px-6'}`}>
            {!effectiveCollapsed && (
              <Link to="/" className="flex items-center gap-3 min-w-0 flex-1">
                <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-primary">
                  <PicklingLogoMark size={20} className="text-primary-foreground" />
                </div>
                <div className="min-w-0">
                  <h1 className="text-lg font-bold text-foreground">Pickle-ing</h1>
                  <p className="text-xs text-muted-foreground truncate">우리동네 피클볼이 한눈에</p>
                </div>
              </Link>
            )}

            <button
              type="button"
              onClick={handleToggleCollapsed}
              className={`h-10 w-10 rounded-lg border border-border bg-background hover:bg-muted transition-smooth flex items-center justify-center text-muted-foreground hover:text-foreground ${
                effectiveCollapsed ? '' : 'ml-3'
              }`}
              aria-label={effectiveCollapsed ? '사이드바 열기' : '사이드바 접기'}
            >
              <Icon name={effectiveCollapsed ? 'ChevronRightIcon' : 'ChevronLeftIcon'} size={20} />
            </button>
          </div>

          <nav className="flex-1 overflow-y-auto">
            <ul className="p-4 space-y-1">
              {sidebarItems.map((item) => {
                const active = isActive(item.path);
                const isProfile = item?.path === '/openplay/user-profile-management';

                if (!isAuthed && isProfile) {
                  return (
                    <li key={`auth-required-${item.label}`}>
                      <button
                        type="button"
                        onClick={handleLoginRequired}
                        className={`w-full relative flex items-center gap-3 py-3 rounded-lg transition-smooth touch-target text-muted-foreground hover:bg-muted hover:text-foreground ${
                          effectiveCollapsed ? 'px-3 justify-center' : 'px-4'
                        }`}
                      >
                        <Icon name={item.icon} variant="outline" size={24} className="transition-smooth" />
                        {!effectiveCollapsed && <span className="flex-1 font-medium text-left">{item.label}</span>}
                      </button>
                    </li>
                  );
                }

                return (
                  <li key={item.path}>
                    <Link
                      to={item.path}
                      className={`
                        w-full relative flex items-center gap-3 py-3 rounded-lg
                        transition-smooth touch-target
                        ${effectiveCollapsed ? 'px-3 justify-center' : 'px-4'}
                        ${active
                          ? 'bg-primary text-primary-foreground shadow-elevation-1'
                          : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                        }
                      `}
                    >
                      <Icon
                        name={item.icon}
                        variant={active ? 'solid' : 'outline'}
                        size={24}
                        className="transition-smooth"
                      />
                      {!effectiveCollapsed && <span className="flex-1 font-medium">{item.label}</span>}
                      {item.badge && item.badge > 0 && (
                        <span
                          className={`
                            flex items-center justify-center min-w-[24px] h-[24px] px-2
                            text-xs font-medium rounded-full
                            ${active ? 'bg-primary-foreground text-primary' : 'bg-primary text-primary-foreground'}
                          `}
                        >
                          {item.badge > 99 ? '99+' : item.badge}
                        </span>
                      )}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </nav>

          <div className="p-4 border-t border-border">
            {isAuthed ? (
              <button
                type="button"
                onClick={handleLogout}
                className={`w-full mb-3 flex items-center justify-center gap-2 py-3 rounded-lg border border-border bg-background hover:bg-muted transition-smooth touch-target ${
                  effectiveCollapsed ? 'px-2' : 'px-4'
                }`}
              >
                <Icon name="ArrowLeftOnRectangleIcon" size={20} />
                {!effectiveCollapsed && <span className="text-sm font-semibold">로그아웃</span>}
              </button>
            ) : (
              <Link
                to="/openplay/user-authentication"
                className={`w-full mb-3 flex items-center justify-center gap-2 py-3 rounded-lg border border-border bg-background hover:bg-muted transition-smooth touch-target ${
                  effectiveCollapsed ? 'px-2' : 'px-4'
                }`}
              >
                <Icon name="ArrowRightOnRectangleIcon" size={20} />
                {!effectiveCollapsed && <span className="text-sm font-semibold">로그인</span>}
              </Link>
            )}

            {isAuthed ? (
              <Link
                to="/openplay/user-profile-management"
                className={`flex items-center gap-3 py-3 rounded-lg bg-muted hover:bg-muted/70 transition-smooth ${
                  effectiveCollapsed ? 'px-3 justify-center' : 'px-4'
                }`}
              >
                <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary text-primary-foreground font-semibold overflow-hidden">
                  {profileAvatarUrl ? (
                    <img src={profileAvatarUrl} alt="profile" className="w-full h-full object-cover" />
                  ) : (
                    avatarInitial
                  )}
                </div>
                {!effectiveCollapsed && (
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">{displayName}</p>
                    <p className="text-xs text-muted-foreground caption">{userRole === 'organizer' ? '주최자' : '플레이어'}</p>
                  </div>
                )}
              </Link>
            ) : (
              <button
                type="button"
                onClick={handleLoginRequired}
                className={`w-full flex items-center gap-3 py-3 rounded-lg bg-muted hover:bg-muted/70 transition-smooth ${
                  effectiveCollapsed ? 'px-3 justify-center' : 'px-4'
                }`}
              >
                <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary text-primary-foreground font-semibold">
                  U
                </div>
                {!effectiveCollapsed && (
                  <div className="flex-1 min-w-0 text-left">
                    <p className="text-sm font-medium text-foreground truncate">사용자</p>
                    <p className="text-xs text-muted-foreground caption">{userRole === 'organizer' ? '주최자' : '플레이어'}</p>
                  </div>
                )}
              </button>
            )}
          </div>
        </div>
      </aside>

      <LoginRequiredModal isOpen={loginRequiredOpen} onClose={() => setLoginRequiredOpen(false)} />
    </>
  );
};

export default BottomTabNavigation;
